import { Card, Group, Space } from '@mantine/core';
import { AnchorButton } from '@root/Design/Primitives';
import { useDi } from '@root/Services/DI';
import { useEffect, useState } from 'react';
import { ResourceTagService } from '../Services/ResourceTagService';
import { BulkTagProps } from './BulkTagProps';
import { TagEditor } from './TagEditor';
import { TagField } from './TagField';
import { CreateLabelKey, LABEL_PREFIX } from '@root/Components/Resources/Gcp/LabelUtils';

export const RemoveLabels = (props: BulkTagProps) => {
    const [labelKeyList, setLabelKeyList] = useState<string[]>([]);
    const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
    const [canAdd, setCanAdd] = useState<boolean>(false);
    const [canRemove, setCanRemove] = useState<boolean>(false);
    const [labelsToRemove, setLabelsToRemove] = useState<string[]>(['']);
    const [selectedValueErrors, setSelectedValueErrors] = useState<Map<number, string>>(new Map());
    const tagSvc = useDi(ResourceTagService);

    useEffect(() => {
        (async () => {
            const keys = await tagSvc.getValidKeys(props.resourceIds);
            const labelKeys = keys.filter((key) => key.startsWith(LABEL_PREFIX)).map((key) => key.substring(6));
            setLabelKeyList(labelKeys);
        })();
    }, [props.resourceIds.count()]);

    useEffect(() => {
        const validForm = formIsValid();
        setDisableSubmit(!validForm);
        setCanRemove(labelsToRemove.length > 1);
        setCanAdd(validForm && labelKeyList.filter((l) => !labelsToRemove.includes(l)).length > 0);
    }, [JSON.stringify(labelsToRemove), labelKeyList.length]);

    const formIsValid = () => {
        return labelsToRemove.filter((l) => l === '').length === 0;
    };

    const addRowHandler = () => {
        setLabelsToRemove((prevState) => {
            let newState = [...prevState];
            newState.push('');
            return newState;
        });
    };

    const removeRowHandler = (index: number) => {
        if (labelsToRemove.length > 1) {
            setLabelsToRemove((prevState) => {
                let newState = [...prevState];
                newState.splice(index, 1);
                return newState;
            });
        }
    };

    const setLabelKey = (index: number, key: string) => {
        setLabelsToRemove((prevState) => {
            let newState = [...prevState];
            let label = newState[index];
            label = key;
            newState.splice(index, 1, label);
            return newState;
        });

        if (labelKeyList.includes(key)) {
            setSelectedValueErrors((prevState) => {
                let newState = new Map(prevState);
                if (newState.has(index)) {
                    newState.delete(index);
                }
                return newState;
            });
        } else {
            setSelectedValueErrors((prevState) => {
                let newState = new Map(prevState);
                newState.set(index, `Value "${key}" does not exist for any of the selected resources.`);
                return newState;
            });
        }
    };

    const labelsWithPrefix = labelsToRemove.map((l) => ({ Key: CreateLabelKey(l) }));
    const tagJob = tagSvc.createTagJob(props.resourceIds, { DeleteTags: labelsWithPrefix });

    return (
        <TagEditor
            onButtonClick={props.onButtonClick}
            companyId={props.companyId}
            resourceIds={props.resourceIds}
            pageTitle={props.pageTitle}
            instructions={props.instructions}
            canSubmit={!disableSubmit}
            setDisableSubmit={setDisableSubmit}
            operationType="remove"
            tagJob={tagJob}
            onTagJobStarting={props.onTagJobStarting}
            onTagJobStarted={props.onTagJobStarted}
            overwriteConflicts={false}
            setOverwriteConflicts={() => null}
        >
            {labelsToRemove.map((label, index) => (
                <div key={`removeLabel${index}`}>
                    <Card withBorder radius="md" p="sm">
                        <Group noWrap grow spacing={0}>
                            <TagField
                                atid={'LabelKeyInput:' + index}
                                key={`RemoveLabelField${index}`}
                                fieldValue={labelsToRemove[index] ?? ''}
                                index={index}
                                count={labelsToRemove.length}
                                valuePickList={labelKeyList.filter((l) => !labelsToRemove.includes(l))}
                                label="Label"
                                setSelectedValue={setLabelKey}
                                handleAddRow={addRowHandler}
                                handleRemoveRow={removeRowHandler}
                                canAdd={canAdd}
                                canRemove={canRemove}
                                receiveInitalFocus={true}
                                type="key"
                            />
                        </Group>
                    </Card>
                    <Space h="sm" />
                </div>
            ))}
            <Card withBorder radius="md" p="sm">
                <AnchorButton text="Add" onClick={addRowHandler} icon={<i className="ti ti-circle-plus"></i>} atid="RemoveLabelsAddAnchor" />
            </Card>
        </TagEditor>
    );
};
